import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDirectMessages } from "@/hooks/useDirectMessages";
import { useMessageReactions } from "@/hooks/useMessageReactions";
import { useThreadReplies } from "@/hooks/useThreadReplies";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, Loader2, X, FileText, Search as SearchIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { LeadMentionPopover } from "@/components/LeadMentionPopover";
import { MessageAttachmentUpload, MessageAttachment, AttachmentPreviewBar, uploadFile, MAX_FILE_SIZE, ALLOWED_TYPES } from "@/components/messaging/MessageAttachmentUpload";
import { MessageInputDropzone } from "@/components/messaging/MessageInputDropzone";
import { MessageAttachmentDisplay } from "@/components/messaging/MessageAttachmentDisplay";
import { MessageReactions } from "@/components/messaging/MessageReactions";
import { MessageHoverActions } from "@/components/messaging/MessageHoverActions";
import { ThreadPreview } from "@/components/messaging/ThreadPreview";
import { ThreadPanel } from "@/components/messaging/ThreadPanel";

interface DirectMessageThreadProps {
  partnerId: string;
  partnerName: string;
  onBack: () => void;
}

interface LinkedLead {
  id: string;
  title: string;
}

export const DirectMessageThread = ({
  partnerId,
  partnerName,
  onBack,
}: DirectMessageThreadProps) => {
  const queryClient = useQueryClient();
  const { messages, isLoadingMessages, sendMessage, isSending, markAsRead } = useDirectMessages(partnerId);
  const [newMessage, setNewMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [linkedLead, setLinkedLead] = useState<LinkedLead | null>(null);
  const [showLeadPopover, setShowLeadPopover] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  
  // Thread replies hook
  const { replies: threadReplies, isLoading: isLoadingReplies, sendReply, isSending: isSendingReply } = useThreadReplies(activeThreadId || undefined, 'direct');
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const LONG_MESSAGE_THRESHOLD = 300;
  const isLongMessage = (text: string) => text.length > LONG_MESSAGE_THRESHOLD;

  // Get current user ID
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id || null);
    });
  }, []);

  // No auto-scroll needed - newest messages are at top

  // Mark unread messages as read
  useEffect(() => {
    if (currentUserId && messages.length > 0) {
      const unreadMessageIds = messages
        .filter(m => m.recipient_id === currentUserId && !m.read_at)
        .map(m => m.id);
      
      if (unreadMessageIds.length > 0) {
        markAsRead(unreadMessageIds);
      }
    }
  }, [messages, currentUserId, markAsRead]);

  // Set up realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('direct-messages-thread')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
        },
        () => {
          // The useQuery will refetch automatically due to queryClient invalidation
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [partnerId]);

  // Handle # key for lead tagging
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === "#" && !showLeadPopover) {
      setShowLeadPopover(true);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    
    try {
      // Include linked lead reference in message if present
      let messageText = newMessage.trim();
      if (linkedLead) {
        messageText = `[Lead: ${linkedLead.title}] ${messageText}`;
      }
      
      await sendMessage({ 
        recipientId: partnerId, 
        message: messageText || "(attachment)",
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setNewMessage("");
      setLinkedLead(null);
      setAttachments([]);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleSelectLead = (lead: { id: string; title: string }) => {
    setLinkedLead({ id: lead.id, title: lead.title });
    setShowLeadPopover(false);
    textareaRef.current?.focus();
  };

  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Parse message to detect and render lead references
  const renderMessage = (message: string) => {
    const leadPattern = /\[Lead: ([^\]]+)\]/g;
    const parts = message.split(leadPattern);
    
    if (parts.length === 1) {
      return <span>{message}</span>;
    }

    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <Badge 
            key={index} 
            variant="secondary" 
            className="mx-1"
          >
            <FileText className="h-3 w-3 mr-1" />
            {part}
          </Badge>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary/10 text-primary text-xs">
            {getInitials(partnerName)}
          </AvatarFallback>
        </Avatar>
        <span className="font-semibold">{partnerName}</span>
        <Button variant="ghost" size="icon" onClick={() => { setShowSearch(!showSearch); setSearchQuery(""); }} title="Search messages" className="ml-auto">
          <SearchIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/50">
          <SearchIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setShowSearch(false); setSearchQuery(""); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {isLoadingMessages ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground text-sm">No messages yet</p>
            <p className="text-muted-foreground text-xs">Start a conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.filter(m => !m.parent_id).filter(m => !searchQuery || m.message.toLowerCase().includes(searchQuery.toLowerCase())).map((msg) => {
              const isOwn = msg.sender_id === currentUserId;
              const senderName = msg.sender?.full_name || msg.sender?.email || "?";
              
              return (
                <div
                  key={msg.id}
                  className={cn(
                    "group relative flex gap-2",
                    isOwn ? "flex-row-reverse" : "flex-row"
                  )}
                >
                  {!isOwn && (
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getInitials(senderName)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="max-w-[70%]">
                    <div
                      className={cn(
                        "rounded-lg px-3 py-2",
                        isOwn
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}
                    >
                      <div 
                        className="text-sm whitespace-pre-wrap break-words max-h-[150px] overflow-y-auto scrollable-content pr-1"
                        style={{ touchAction: 'pan-y' }}
                      >
                        {renderMessage(msg.message)}
                      </div>
                      {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                        <MessageAttachmentDisplay attachments={msg.attachments as any[]} />
                      )}
                      <p className={cn(
                        "text-xs mt-1",
                        isOwn ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}>
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {/* Reactions */}
                    {currentUserId && (
                      <MessageReactions
                        messageId={msg.id}
                        messageType="direct"
                        currentUserId={currentUserId}
                      />
                    )}
                    {/* Thread Preview */}
                    {(msg.reply_count || 0) > 0 && (
                      <ThreadPreview
                        replyCount={msg.reply_count || 0}
                        onClick={() => setActiveThreadId(msg.id)}
                      />
                    )}
                  </div>
                  {/* Hover Actions */}
                  {currentUserId && (
                    <MessageHoverActions
                      onReply={() => setActiveThreadId(msg.id)}
                      onAddReaction={async (emoji) => {
                        const { data: session } = await supabase.auth.getSession();
                        if (!session?.session?.user) return;
                        
                        await supabase.from("message_reactions").insert({
                          message_id: msg.id,
                          message_type: 'direct',
                          user_id: session.session.user.id,
                          emoji,
                        });
                        // Invalidate the reactions query to trigger refetch
                        queryClient.invalidateQueries({ queryKey: ["message-reactions", msg.id, "direct"] });
                      }}
                      className={cn(
                        isOwn ? "left-2 right-auto" : "right-2"
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Thread Panel */}
      {activeThreadId && (
        <div className="w-80 border-l flex-shrink-0">
          <ThreadPanel
            parentMessage={messages.find(m => m.id === activeThreadId)!}
            replies={threadReplies as any[]}
            currentUserId={currentUserId || ''}
            messageType="direct"
            onClose={() => setActiveThreadId(null)}
            onSendReply={async (message) => {
              await sendReply({ message, recipientId: partnerId });
            }}
            isLoading={isLoadingReplies}
            isSending={isSendingReply}
          />
        </div>
      )}

      {/* Input */}
      <MessageInputDropzone
        onFilesDropped={async (files) => {
          const newAttachments: MessageAttachment[] = [];
          for (const file of files) {
            const attachment = await uploadFile(file);
            if (attachment) newAttachments.push(attachment);
          }
          if (newAttachments.length > 0) {
            setAttachments(prev => [...prev, ...newAttachments]);
          }
        }}
        disabled={isSending}
      >
      <div className="p-4 border-t">
        {/* Linked Lead Badge */}
        {linkedLead && (
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {linkedLead.title}
              <button 
                onClick={() => setLinkedLead(null)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          </div>
        )}
        
        <AttachmentPreviewBar
          attachments={attachments}
          onRemove={(id) => setAttachments(attachments.filter(a => a.id !== id))}
        />
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="min-h-[120px] pr-3 pb-12 resize-none"
            disabled={isSending}
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-1">
            <MessageAttachmentUpload
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              disabled={isSending}
            />
            <LeadMentionPopover
              onSelectLead={handleSelectLead}
              open={showLeadPopover}
              onOpenChange={setShowLeadPopover}
            />
            <Button
              size="icon"
              className="h-8 w-8"
              onClick={handleSend}
              disabled={(!newMessage.trim() && attachments.length === 0) || isSending}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Press Enter to send, Shift+Enter for new line, # to link a lead, or drag & drop files
        </p>
      </div>
      </MessageInputDropzone>
    </div>
  );
};
